import { memo, ReactElement, ReactNode, useMemo } from 'react';

import { motion, AnimatePresence } from 'framer-motion';

import { useFormContext } from './form-provider';
import { extractWatchingIdsFromCondition, shouldDisplayField } from './form-utils';
import { DisplayCondition, FormExternalConditionData } from './types';

export type FormConditionalProps = {
  condition: DisplayCondition;
  data?: FormExternalConditionData;
  children: ReactNode | ((isVisible: boolean) => ReactElement);
};

export const FormConditional = memo<FormConditionalProps>(({ children, condition, data = {} }) => {
  const {
    store: { useWatchValues },
  } = useFormContext();

  const keys = useMemo(() => extractWatchingIdsFromCondition(condition), [condition]);
  const values = useWatchValues(keys);
  const isVisible = shouldDisplayField(condition, data, values);

  if (typeof children === 'function') {
    return children(isVisible);
  }

  return (
    <AnimatePresence initial={false}>
      {isVisible && (
        <motion.div
          transition={{ duration: 0.15 }}
          animate={{ opacity: 1, scale: 1 }}
          initial={{ opacity: 0, scale: 0.95 }}
          exit={{
            opacity: 0,
            scale: 0.9,
            height: 0,
            overflow: 'hidden',
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
});

FormConditional.displayName = 'Form.Conditional';
